import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import NewOrderHeader from "./NewOrderHeader";
import useCart from "./useCart";
import useNewOrderState, { maxStep } from "./useNewOrderState";
import CartSection from "./CartSection";
import ProductSearchSection from "./ProductSearchSection";

/**
 * Step 1 — Carrinho: search produtos + carrinho com totais.
 */
export default function NewOrderCartPage() {
  const navigate = useNavigate();
  const { activeId } = useAuth();
  const [state, update] = useNewOrderState();
  const cartH = useCart();

  // Product search local state (não persistido — é específico desta página)
  const [productIdSearch, setProductIdSearch] = useState("");
  const [productNameSearch, setProductNameSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [buyerType, setBuyerType] = useState("CF");
  const [productQuantities, setProductQuantities] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTriggered, setSearchTriggered] = useState(false);

  // Quando o cliente alvo muda (via X-View-As), recarregar o cart.
  useEffect(() => {
    cartH.load?.();
    setProducts([]);
    setSearchTriggered(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const searchProducts = async () => {
    setSearchTriggered(true);
    setLoadingProducts(true);
    try {
      const { data } = await api.post("/encomendas/searchProducts", {
        product_id: productIdSearch,
        product_name: productNameSearch,
      });
      if (data.success) {
        setProducts(data.data || []);
        setBuyerType(data.id_tipo_cliente || "CF");
      } else {
        setProducts([]);
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("searchProducts failed", e);
      setProducts([]);
    } finally {
      setProductQuantities({});
      setLoadingProducts(false);
    }
  };

  const setQty = (id, val) =>
    setProductQuantities((prev) => ({ ...prev, [id]: Math.max(0, parseInt(val, 10) || 0) }));

  const onAddProductToCart = async (prod) => {
    const qty = productQuantities[prod.ID_Produto] || 0;
    if (qty < 1) return;
    await cartH.add(prod.ID_Produto, qty);
    setProductQuantities((prev) => ({ ...prev, [prod.ID_Produto]: 0 }));
  };

  const goNext = () => {
    update({ maxStep: maxStep(state.maxStep, "checkout") });
    navigate("/encomendas/nova/checkout");
  };

  const cartEmpty = cartH.cart.length === 0;

  return (
    <div data-testid="new-order-cart-page">
      <NewOrderHeader maxStepReached={state.maxStep || "cart"} />

      <div className="mb-4">
        <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}>Carrinho</h1>
        <p className="text-sm text-[#6B7280] mt-1">Procure produtos e adicione ao carrinho. Avance para os dados de entrega quando estiver pronto.</p>
      </div>

      <ProductSearchSection
        productIdSearch={productIdSearch} setProductIdSearch={setProductIdSearch}
        productNameSearch={productNameSearch} setProductNameSearch={setProductNameSearch}
        onSearch={searchProducts} loadingProducts={loadingProducts}
        searchTriggered={searchTriggered} products={products} buyerType={buyerType}
        productQuantities={productQuantities} onSetQty={setQty}
        onAddToCart={onAddProductToCart}
      />

      <CartSection
        cart={cartH.cart} loading={cartH.loading}
        total={cartH.total} netPoints={cartH.netPoints}
        onUpdateQty={cartH.updateQty} onRemove={cartH.remove} onClear={cartH.clearAll}
      />

      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5 flex items-center justify-between gap-4 flex-wrap mt-4">
        <div className="text-sm text-[#6B7280]">
          {cartEmpty ? "Adicione pelo menos 1 produto para avançar." : `${cartH.cart.length} ${cartH.cart.length === 1 ? "produto" : "produtos"} no carrinho.`}
        </div>
        <button type="button" onClick={goNext} disabled={cartEmpty}
          data-testid="new-order-cart-next"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#0F1829] text-white text-sm font-semibold rounded-md hover:bg-[#001f6e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          Avançar para Dados <ArrowRight size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
